// syncEngine.js — incremental pull from Supabase into local PGlite.
//
// Strategy:
//   - Each table tracks its own last_synced_at in _sync_state.
//   - Pull runs: SELECT * WHERE updated_at > last_synced_at ORDER BY updated_at ASC
//   - Rows are upserted into PGlite (ON CONFLICT id DO UPDATE).
//   - On first run (last_synced_at = null) the full table is pulled in batches.
//   - call_log is capped to maxAgeDays to avoid pulling all history.
//
// Phase 3 will add push (local mutations → Supabase). The _mutation_queue
// infrastructure is already in localDb.js ready to be wired in.

import { supabase } from './supabase'
import { getLocalDb, getSyncState, setSyncState, vacuumLocal, maybeCompactLocal, isDesktopDb, initLocalDb } from './localDb'
import { SYNC_TABLES, SYNC_ORDER } from './syncConfig'

// Rows that genuinely changed get re-upserted (ON CONFLICT DO UPDATE), and each
// real update leaves a Postgres dead tuple. Reclaim them periodically so the
// persisted store stabilizes at working-set size instead of ballooning (it
// twice hit >1 GB). Throttled to at most once an hour — VACUUM is cheap but not
// worth running on every 45 s health-check sync.
const VACUUM_INTERVAL_MS = 60 * 60 * 1000
let _lastVacuumAt = 0

// ─── Public API ───────────────────────────────────────────────────────────────

// Pull all tables incrementally (only rows changed since last sync).
// Called on startup and on reconnect. Tables run sequentially per SYNC_ORDER.
export async function syncAll({ onProgress } = {}) {
  const db = getLocalDb()
  if (!db) return { skipped: true, reason: 'localDb not ready' }

  const results = {}
  const ordered = SYNC_ORDER.map(n => SYNC_TABLES.find(t => t.name === n)).filter(Boolean)

  for (const tableConfig of ordered) {
    try {
      const pulled = await _pullTable(tableConfig)
      results[tableConfig.name] = { pulled }
      onProgress?.({ table: tableConfig.name, pulled })
    } catch (err) {
      results[tableConfig.name] = { error: err?.message }
      console.warn(`[syncEngine] ${tableConfig.name} pull failed:`, err?.message)
      // Record error in sync state but continue with next table.
      await setSyncState(tableConfig.name, {
        lastSyncedAt: (await getSyncState(tableConfig.name))?.last_synced_at ?? null,
        rowCount: 0,
        error: err?.message,
      })
    }
  }

  // Periodic maintenance (throttled). Runs after the pull so it never delays
  // data landing; failures are swallowed inside both helpers. Compaction is
  // checked FIRST: plain VACUUM frees space for reuse but never shrinks the
  // IndexedDB files, and on a badly ratcheted store (>300 MB, the threshold
  // inside maybeCompactLocal) it grinds for many minutes — during which a
  // deploy-triggered reload can kill the tab before VACUUM FULL ever runs.
  // A completed VACUUM FULL leaves zero dead tuples, making the plain pass
  // redundant that round.
  const now = Date.now()
  if (now - _lastVacuumAt >= VACUUM_INTERVAL_MS) {
    _lastVacuumAt = now
    const compacted = await maybeCompactLocal()
    if (!compacted) await vacuumLocal()
  }

  // Ghost-row reconciliation (desktop only): pull sync only upserts, so rows
  // deleted in Supabase linger in the mirror — invisible when local was just a
  // fallback, but user-visible now that desktop reads paint local-first. One
  // table per sync round, each at most once per 24h; failures never block sync.
  if (isDesktopDb()) {
    try {
      await _reconcileNextTable()
    } catch (err) {
      console.warn('[syncEngine] reconciliation failed:', err?.message)
    }
  }

  return results
}

// Pull a single table — useful for targeted refresh after a mutation.
export async function syncTable(tableName) {
  const config = SYNC_TABLES.find(t => t.name === tableName)
  if (!config) throw new Error(`[syncEngine] unknown table: ${tableName}`)
  const db = getLocalDb()
  if (!db) return 0
  return _pullTable(config)
}

// Bound any local-DB read so a wedged PGlite WASM worker can never hang a
// caller. These are user-facing reads (list-hook fallbacks, pickers); a stalled
// query must resolve empty fast, not freeze the UI. 4s is well beyond a healthy
// local query (single-digit ms) but short enough to feel responsive.
const LOCAL_QUERY_TIMEOUT_MS = 4000
function _boundLocal(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('local query timeout')), LOCAL_QUERY_TIMEOUT_MS)),
  ])
}

// Shared race helper for user-facing reads (autocomplete pickers etc.) that
// want to fail fast rather than wait out the 30s global Supabase timeout.
export function raceTimeout(promise, ms = 7000, label = 'query') {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timeout`)), ms)),
  ])
}

// Read a row from local PGlite. Returns the full Supabase row (from _data).
// Falls back to null if not found or db not ready.
export async function localGet(tableName, id) {
  const db = getLocalDb()
  if (!db) return null
  try {
    const { rows } = await _boundLocal(db.query(
      `SELECT _data FROM ${tableName} WHERE id = $1`,
      [String(id)]
    ))
    return rows[0]?._data ?? null
  } catch {
    return null
  }
}

// List rows from local PGlite with optional WHERE clause.
// Returns array of full Supabase rows (unwrapped from _data).
export async function localList(tableName, { where = '', params = [], orderBy = 'updated_at DESC', limit = 1000 } = {}) {
  const db = getLocalDb()
  if (!db) return []
  try {
    const whereClause = where ? `WHERE ${where}` : ''
    const { rows } = await _boundLocal(db.query(
      `SELECT _data FROM ${tableName} ${whereClause} ORDER BY ${orderBy} LIMIT ${limit}`,
      params
    ))
    return rows.map(r => r._data)
  } catch (err) {
    console.warn(`[syncEngine] localList ${tableName}:`, err?.message)
    return []
  }
}

// Upsert a single row locally — called optimistically from mutation hooks (Phase 3).
export async function localUpsert(tableName, row) {
  const db = getLocalDb()
  if (!db || !row?.id) return
  const config = SYNC_TABLES.find(t => t.name === tableName)
  if (!config) return
  await _upsertRow(db, tableName, config, row)
}

// Upsert many rows in ONE statement. The list write-through mirrors a whole page
// of rows on every fetch — and the inbox fetches on a 30s poll, on focus, and on
// every Realtime burst. Doing that as N separate localUpsert() calls meant ~100
// PGlite round trips per refresh; in the browser PGlite runs on the main thread,
// so that showed up as the inbox periodically stalling. One multi-row INSERT with
// the same skip-if-unchanged guard costs a single round trip.
export async function localUpsertMany(tableName, rows) {
  const db = getLocalDb()
  if (!db) return
  const config = SYNC_TABLES.find(t => t.name === tableName)
  if (!config) return
  // Dedupe by id, last write wins. Postgres rejects a multi-row upsert that hits
  // the same conflict target twice ("cannot affect row a second time"), and the
  // caller's page can legitimately contain a repeat after a merge.
  const byId = new Map()
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r?.id) byId.set(String(r.id), r)
  }
  const valid = [...byId.values()]
  if (valid.length === 0) return

  const { indexedCols = [] } = config
  const cols = ['id', 'updated_at', ...indexedCols, '_data']
  const perRow = cols.length

  // Chunked so a very large page can't build a statement with more parameters
  // than the driver will accept.
  const CHUNK = 100
  for (let start = 0; start < valid.length; start += CHUNK) {
    const chunk = valid.slice(start, start + CHUNK)
    const params = []
    const tuples = chunk.map((row, i) => {
      params.push(
        String(row.id ?? ''),
        row.updated_at ?? null,
        ...indexedCols.map(c => _coerce(row[c])),
        JSON.stringify(row),
      )
      return `(${cols.map((_, j) => `$${i * perRow + j + 1}`).join(', ')})`
    })
    const updates = cols.slice(1).map(c => `${c} = EXCLUDED.${c}`).join(', ')
    try {
      await db.query(
        `INSERT INTO ${tableName} (${cols.join(', ')})
         VALUES ${tuples.join(', ')}
         ON CONFLICT (id) DO UPDATE SET ${updates}
         WHERE ${tableName}._data IS DISTINCT FROM EXCLUDED._data`,
        params
      )
    } catch (err) {
      console.warn(`[syncEngine] localUpsertMany ${tableName}:`, err?.message)
      return
    }
  }
}

// Remove a row locally — called optimistically on delete (Phase 3).
export async function localDelete(tableName, id) {
  const db = getLocalDb()
  if (!db) return
  await db.query(`DELETE FROM ${tableName} WHERE id = $1`, [String(id)])
}

// Safe wrapper — never throws; returns [] when PGlite is unavailable or table missing.
// Use in hook error branches as a read fallback when Supabase is unreachable.
export async function localFallback(tableName, options = {}) {
  try {
    return await localList(tableName, options)
  } catch {
    return []
  }
}

// Local-first paint gate — desktop shell only (on-disk mirror is fast and
// durable; browser IndexedDB stays fallback-only). Returns rows to paint
// immediately, or null when local can't be trusted: never-synced table (empty
// ≠ "no data"), empty result, browser, or any local error. Callers paint these
// rows and still run the network fetch — its result replaces them wholesale,
// so staleness self-heals within one revalidate.
export async function localFirstRows(tableName, options = {}) {
  if (!isDesktopDb() || !tableName) return null
  try {
    // A route mounting right at boot can beat the (fast, ~100s of ms) IPC
    // migration handshake — wait for readiness instead of silently skipping
    // the paint. Bounded so a wedged bridge degrades to network-only.
    if (!getLocalDb()) await _boundLocal(initLocalDb())
    if (!getLocalDb()) return null
    const st = await _boundLocal(getSyncState(tableName))
    if (!st?.last_synced_at) return null
    const rows = await localList(tableName, options)
    return rows.length ? rows : null
  } catch {
    return null
  }
}

// ─── Sync status — readable by UI ────────────────────────────────────────────

export async function getSyncSummary() {
  const db = getLocalDb()
  if (!db) return null
  const { rows } = await db.query(`SELECT * FROM _sync_state ORDER BY table_name`)
  return rows
}

// ─── Internal ─────────────────────────────────────────────────────────────────

async function _pullTable(tableConfig) {
  const { name, batchSize = 200, maxAgeDays, updatedAtCol = 'updated_at' } = tableConfig
  const db = getLocalDb()

  const state = await getSyncState(name)
  let lastSync = state?.last_synced_at ?? null

  // PGlite formats TIMESTAMPTZ using the browser's UTC offset (e.g. "gmt-0700")
  // which Postgres/PostgREST rejects. Normalize to clean UTC ISO string.
  if (lastSync) lastSync = new Date(lastSync).toISOString()

  // For tables with maxAgeDays, never pull beyond the age cap on first run.
  if (!lastSync && maxAgeDays) {
    const cutoff = new Date(Date.now() - maxAgeDays * 86_400_000)
    lastSync = cutoff.toISOString()
  }

  const pullFrom = lastSync ?? '2020-01-01T00:00:00Z'
  let offset = 0
  let totalPulled = 0
  let hasMore = true
  const syncStart = new Date().toISOString()

  while (hasMore) {
    let query = supabase
      .from(name)
      .select('*')
      .gt(updatedAtCol, pullFrom)
      .order(updatedAtCol, { ascending: true })
      .range(offset, offset + batchSize - 1)

    const { data, error } = await query
    if (error) throw error
    if (!data?.length) { hasMore = false; break }

    for (const row of data) {
      await _upsertRow(db, name, tableConfig, row)
    }

    totalPulled += data.length
    offset += batchSize
    hasMore = data.length === batchSize
  }

  // Update sync state with completion timestamp and current local row count.
  const { rows: countRows } = await db.query(
    `SELECT COUNT(*) AS c FROM ${name}`
  )
  const rowCount = parseInt(countRows[0]?.c ?? '0', 10)

  await setSyncState(name, {
    lastSyncedAt: syncStart,
    rowCount,
    error: null,
  })

  if (totalPulled > 0) {
    console.info(`[syncEngine] ${name}: pulled ${totalPulled} row(s), total local: ${rowCount}`)
  }

  return totalPulled
}

async function _upsertRow(db, tableName, tableConfig, row) {
  const { indexedCols = [] } = tableConfig

  // Build column list: id + updated_at + indexed cols + _data
  const cols = ['id', 'updated_at', ...indexedCols, '_data']
  const vals = [
    String(row.id ?? ''),
    row.updated_at ?? null,
    ...indexedCols.map(c => _coerce(row[c])),
    JSON.stringify(row),
  ]

  const placeholders = cols.map((_, i) => `$${i + 1}`).join(', ')
  const updates = cols
    .slice(1)  // skip id (PK)
    .map(c => `${c} = EXCLUDED.${c}`)
    .join(', ')

  // Skip-if-unchanged guard: most upserts are no-op mirrors (the inbox
  // write-through re-sends the same ~50 rows every poll/focus in every open
  // tab). Without the WHERE clause each one wrote a new row version AND
  // re-TOASTed the full _data JSONB — that churn was the source of the 1 GB+
  // IndexedDB bloat (a single 621 MB TOAST file + ~500 MB of WAL). JSONB
  // comparison is by value, so key order/whitespace never forces a write.
  // _data holds the entire row, so equal _data ⇒ every derived column equal.
  await db.query(
    `INSERT INTO ${tableName} (${cols.join(', ')})
     VALUES (${placeholders})
     ON CONFLICT (id) DO UPDATE SET ${updates}
     WHERE ${tableName}._data IS DISTINCT FROM EXCLUDED._data`,
    vals
  )
}

// Coerce JS values to types PGlite accepts cleanly.
function _coerce(val) {
  if (val === undefined) return null
  if (val !== null && typeof val === 'object') return JSON.stringify(val)
  return val
}

// ─── Ghost-row reconciliation (desktop local-first) ─────────────────────────

const RECONCILE_INTERVAL_MS = 24 * 60 * 60 * 1000
const RECONCILE_ID_PAGE = 1000
const RECONCILE_DELETE_CHUNK = 500

// Pick the stalest table that hasn't reconciled in 24h and reconcile just it.
// Stamps live in _meta ('reconciled:<table>') so no schema migration is needed
// and the rotation survives reloads.
async function _reconcileNextTable() {
  const db = getLocalDb()
  if (!db) return
  const { rows } = await db.query(
    `SELECT key, value FROM _meta WHERE key LIKE 'reconciled:%'`
  )
  const stamps = Object.fromEntries(rows.map(r => [r.key.slice('reconciled:'.length), Number(r.value) || 0]))
  const now = Date.now()
  const due = SYNC_ORDER
    .map(n => SYNC_TABLES.find(t => t.name === n))
    .filter(Boolean)
    .filter(t => now - (stamps[t.name] || 0) >= RECONCILE_INTERVAL_MS)
    .sort((a, b) => (stamps[a.name] || 0) - (stamps[b.name] || 0))
  const next = due[0]
  if (!next) return
  // Stamp BEFORE the work so a failing table can't wedge the rotation.
  await db.query(
    `INSERT INTO _meta (key, value) VALUES ($1, $2)
     ON CONFLICT (key) DO UPDATE SET value = EXCLUDED.value`,
    [`reconciled:${next.name}`, String(now)]
  )
  const removed = await _reconcileTable(next)
  if (removed > 0) {
    console.info(`[syncEngine] reconciled ${next.name}: removed ${removed} ghost row(s)`)
  }
}

// Diff local ids against Supabase and delete local rows that no longer exist
// remotely. For age-capped tables the remote id scan is restricted to the
// maxAgeDays window, local rows older than the window are pruned outright, and
// only in-window local rows are ghost candidates (out-of-window absence from
// the remote set is expected, not a delete signal).
// Exported for tests only — production entry point is syncAll().
export async function _reconcileTable(tableConfig) {
  const db = getLocalDb()
  if (!db) return 0
  const { name, maxAgeDays, updatedAtCol = 'updated_at' } = tableConfig
  const cutoffIso = maxAgeDays
    ? new Date(Date.now() - maxAgeDays * 86_400_000).toISOString()
    : null

  // Remote ids (paged, ids only — cheap even for thousands of rows).
  const remoteIds = new Set()
  let offset = 0
  for (;;) {
    let query = supabase
      .from(name)
      .select('id')
      .order('id', { ascending: true })
      .range(offset, offset + RECONCILE_ID_PAGE - 1)
    if (cutoffIso) query = query.gt(updatedAtCol, cutoffIso)
    const { data, error } = await query
    if (error) throw error
    for (const r of data || []) remoteIds.add(String(r.id))
    if (!data || data.length < RECONCILE_ID_PAGE) break
    offset += RECONCILE_ID_PAGE
  }

  let removed = 0

  // Age-prune first so out-of-window rows never look like ghosts. The
  // timestamp lives in _data (updatedAtCol may not be an indexed column).
  if (cutoffIso) {
    const res = await db.query(
      `DELETE FROM ${name}
       WHERE (_data->>'${updatedAtCol}') IS NOT NULL
         AND (_data->>'${updatedAtCol}')::timestamptz < $1::timestamptz`,
      [cutoffIso]
    )
    removed += res.affectedRows || 0
  }

  // Local ghost candidates: in-window rows (or all rows for uncapped tables).
  const candidateSql = cutoffIso
    ? `SELECT id FROM ${name}
       WHERE (_data->>'${updatedAtCol}') IS NOT NULL
         AND (_data->>'${updatedAtCol}')::timestamptz >= $1::timestamptz`
    : `SELECT id FROM ${name}`
  const { rows: localRows } = await db.query(candidateSql, cutoffIso ? [cutoffIso] : [])
  const ghosts = localRows.map(r => String(r.id)).filter(id => !remoteIds.has(id))

  for (let i = 0; i < ghosts.length; i += RECONCILE_DELETE_CHUNK) {
    const chunk = ghosts.slice(i, i + RECONCILE_DELETE_CHUNK)
    const res = await db.query(
      `DELETE FROM ${name} WHERE id = ANY($1::text[])`,
      [chunk]
    )
    removed += res.affectedRows || 0
  }

  return removed
}
